import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  MapPin,
  Info,
  Calendar,
  Clock,
  CheckCircle,
  X,
  Truck,
  ArrowRight,
  Navigation,
  Search,
} from "lucide-react";
import { Driver } from "../../../../../services/api/LaborderService/types";
import DriverSelect from "../OnlineOrder/SelectDriver";

interface ClinicLabtestOrderDetailsViewProps {
  orderDetails: any;
  onBack: () => void;
  onAssignDriver: (orderId: number) => void;
  onForwardToLab: (orderId: number) => void;
  drivers: Driver[];
  selectedDriver: string | undefined;
  onDriverSelect: (driver: Driver) => void;
}

const ClinicLabtestOrderDetailsView: React.FC<ClinicLabtestOrderDetailsViewProps> = ({
  orderDetails,
  onBack,
  onAssignDriver,
  onForwardToLab,
  drivers,
  selectedDriver,
  onDriverSelect,
}) => {
  const [showOrderDetails, setShowOrderDetails] = useState(true);
  const [showPrescriptions, setShowPrescriptions] = useState(false);
  const [showReports, setShowReports] = useState(true);
  const [showBill, setShowBill] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [forwardLoading, setForwardLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [showRiderModal, setShowRiderModal] = useState(false);
  const [riderSearch, setRiderSearch] = useState("");

  const currentStatus = orderDetails.orderStatus?.toLowerCase() || "";
  const isClinicAssigned = currentStatus === "clinic_assigned";
  const isPickupCompleted = currentStatus === "pickup_completed";

  const getStatusStyle = (status: string) => {
    if (!status) return "text-slate-500 bg-slate-50 border border-slate-200";
    const clean = status.toLowerCase();
    if (clean === "clinic_assigned") return "text-amber-600 bg-amber-50 border border-amber-200";
    if (clean === "pickup_assigned" || clean === "out_for_pickup") return "text-indigo-600 bg-indigo-50 border border-indigo-200";
    if (clean === "pickup_completed") return "text-cyan-600 bg-cyan-50 border border-cyan-200";
    if (clean === "moved_to_lab") return "text-blue-600 bg-blue-50 border border-blue-200";
    if (clean === "report_generated") return "text-emerald-600 bg-emerald-50 border border-emerald-200";
    if (clean === "cancelled") return "text-rose-600 bg-rose-50 border border-rose-200";
    return "text-blue-600 bg-blue-50 border border-blue-200";
  };

  const getStatusLabel = (status: string) => {
    if (!status) return "—";
    return status.replace(/_/g, " ").toUpperCase();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isPDF = (url?: string) => url?.toLowerCase().includes(".pdf");

  const handleAssignClick = async () => {
    setAssignLoading(true);
    await onAssignDriver(orderDetails.id);
    setAssignLoading(false);
  };

  const handleForwardClick = async () => {
    setForwardLoading(true);
    await onForwardToLab(orderDetails.id);
    setForwardLoading(false);
  };

  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden relative">
      <div className="flex-1 overflow-auto p-5">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700"
        >
          <ChevronLeft size={16} /> Back to Labtest Orders
        </button>

        <div className="flex flex-col lg:flex-row gap-5">
          {/* Left Column */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            {/* Patient & Order Information */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <button
                onClick={() => setShowOrderDetails(!showOrderDetails)}
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="uppercase tracking-wider">Patient & Order Details</span>
                  <span
                    className={`px-2.5 py-1 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${getStatusStyle(
                      orderDetails.orderStatus
                    )}`}
                  >
                    {getStatusLabel(orderDetails.orderStatus)}
                  </span>
                </div>
                {showOrderDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showOrderDetails && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-semibold text-slate-600">
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">
                        Order ID
                      </span>
                      <span className="text-slate-800 font-extrabold text-sm">#{orderDetails.orderNumber}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">
                        Order Date
                      </span>
                      <span className="text-slate-800 font-bold">{formatDate(orderDetails.orderDate)}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">
                        Ordered By
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {orderDetails.patientDetails?.patientProfileImage && (
                          <img
                            src={orderDetails.patientDetails.patientProfileImage}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover border border-slate-200"
                          />
                        )}
                        <div>
                          <div className="text-slate-800 font-bold">{orderDetails.patientDetails?.patientName}</div>
                          <div className="text-[0.6rem] text-slate-400 font-semibold">
                            +91 {orderDetails.patientPhoneNumber || orderDetails.patientDetails?.patientMobileNumber}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">
                        Payment
                      </span>
                      <span className="text-slate-800 font-bold block">{orderDetails.paymentMethod || "COD"}</span>
                      <span
                        className={`text-[0.6rem] font-bold ${
                          orderDetails.paymentStatus?.toLowerCase() === "success"
                            ? "text-emerald-600"
                            : "text-amber-600"
                        }`}
                      >
                        {orderDetails.paymentStatus || "Pending"}
                      </span>
                    </div>
                  </div>

                  {/* Address details */}
                  <div className="mt-4 pt-3 border-t border-slate-50">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1 font-bold">
                      Sample Collection Address
                    </span>
                    <div className="text-xs text-slate-700 flex items-start gap-1 font-semibold">
                      <MapPin size={13} className="text-rose-500 mt-0.5 flex-shrink-0" />
                      <span>
                        {orderDetails.orderAddress} (Pincode: {orderDetails.orderPincode})
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rider Assignment Section */}
            {(isClinicAssigned || orderDetails.deliveryPersonDetails) && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex items-center gap-2">
                    <Navigation size={15} className="text-slate-400" />
                    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Rider Assignment</span>
                  </div>
                  {isClinicAssigned && selectedDriver && (
                    <button
                      onClick={() => setShowRiderModal(true)}
                      className="text-[10px] font-bold text-emerald-600 border-none bg-transparent cursor-pointer hover:underline"
                    >
                      Change Rider
                    </button>
                  )}
                </div>
                <div className="p-5">
                  {orderDetails.deliveryPersonDetails ? (
                    <div className="flex items-center justify-between p-3 bg-emerald-50/25 border border-emerald-100 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 flex-shrink-0">
                          <CheckCircle size={16} />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-slate-800">
                            {orderDetails.deliveryPersonDetails.deliveryPersonName}
                          </div>
                          <div className="text-[10px] text-slate-500 font-semibold mt-0.5">
                            Phone: +91 {orderDetails.deliveryPersonDetails.deliveryPersonMobileNumber}
                            {orderDetails.deliveryPersonDetails.deliveryPersonVehicleNumber &&
                              ` • Vehicle: ${orderDetails.deliveryPersonDetails.deliveryPersonVehicleNumber}`}
                          </div>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-[9px] font-bold text-emerald-700 border border-emerald-100">
                        Assigned
                      </span>
                    </div>
                  ) : isClinicAssigned ? (
                    selectedDriver ? (
                      (() => {
                        const activeDriver = drivers.find((d) => d.id === selectedDriver);
                        return activeDriver ? (
                          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl">
                            <div className="flex items-center gap-3">
                              <img
                                src={activeDriver.profilePicture || "https://via.placeholder.com/40"}
                                alt=""
                                className="w-8 h-8 rounded-full object-cover border border-slate-200 bg-white"
                              />
                              <div>
                                <div className="text-xs font-bold text-slate-800">{activeDriver.name}</div>
                                <div className="text-[10px] text-slate-500 font-semibold mt-0.5">
                                  📞 {activeDriver.mobileNumber}
                                </div>
                              </div>
                            </div>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-[9px] font-bold text-amber-700 border border-amber-100">
                              {activeDriver.pendingOrdersCount} active orders
                            </span>
                          </div>
                        ) : null;
                      })()
                    ) : (
                      <div className="flex items-center justify-between py-3.5 text-center border border-dashed border-slate-200 rounded-xl px-4 bg-slate-50/20">
                        <div className="flex items-center gap-2">
                          <Navigation className="text-slate-400" size={16} />
                          <span className="text-xs font-semibold text-slate-400">
                            No rider assigned yet
                          </span>
                        </div>
                        <button
                          onClick={() => setShowRiderModal(true)}
                          className="px-4 py-2 text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-all border-none cursor-pointer shadow-sm"
                        >
                          Assign Rider
                        </button>
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            )}

            {/* Forward to Lab Section (pickup_completed) */}
            {isPickupCompleted && (
              <div className="bg-white rounded-xl border border-blue-200 p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Sample Pickup Completed</h3>
                    <p className="text-[11px] text-slate-500 font-semibold mt-1">
                      The rider has picked up the samples from the patient. You can forward the samples to the
                      diagnostic lab using the button in the sidebar.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Booked Tests Table */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
                Booked Labtests
              </h3>
              <div className="grid grid-cols-[3fr_2fr_1.5fr_1.5fr] gap-2 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-50 pb-2 mb-2">
                <div>Test / Package Name</div>
                <div>Ordered For (Patient)</div>
                <div>Collection Date</div>
                <div className="text-right">Collection Slot</div>
              </div>
              <div className="space-y-2">
                {Array.isArray(orderDetails.labtestItemsDetails) && orderDetails.labtestItemsDetails.length > 0 ? (
                  orderDetails.labtestItemsDetails.map((item: any, idx: number) => (
                    <div
                      key={idx}
                      className="grid grid-cols-[3fr_2fr_1.5fr_1.5fr] gap-2 items-center py-2.5 px-2 rounded-lg border border-slate-50 bg-slate-50/30 hover:bg-slate-50 transition-colors"
                    >
                      <div className="text-xs font-bold text-slate-800">{item.labtestName || "Labtest"}</div>
                      <div className="flex items-center gap-2">
                        {item.bookedPatientProfileImage && (
                          <img
                            src={item.bookedPatientProfileImage}
                            alt=""
                            className="w-5 h-5 rounded-full object-cover border border-slate-200"
                          />
                        )}
                        <div>
                          <div className="text-xs font-bold text-slate-700">{item.bookedPatientName || "—"}</div>
                          {item.bookedPatientAge && (
                            <div className="text-[9px] text-slate-400 font-semibold">{item.bookedPatientAge}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-slate-600 font-bold flex items-center gap-1">
                        <Calendar size={12} className="text-slate-400" />
                        {item.date ? new Date(item.date).toLocaleDateString("en-IN") : "—"}
                      </div>
                      <div className="text-xs text-slate-600 font-bold text-right flex items-center gap-1 justify-end">
                        <Clock size={12} className="text-slate-400" />
                        {item.timeSlot || "—"}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 italic">No labtests mapped for this order yet.</p>
                )}
              </div>
            </div>

            {/* Prescriptions */}
            {((orderDetails.labtestPrescriptionDetails && orderDetails.labtestPrescriptionDetails.length > 0) ||
              orderDetails.hasPrescription) && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowPrescriptions(!showPrescriptions)}
                >
                  <span className="uppercase tracking-wider">Patient Prescriptions</span>
                  {showPrescriptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showPrescriptions && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 flex flex-wrap gap-4">
                    {orderDetails.labtestPrescriptionDetails && orderDetails.labtestPrescriptionDetails.length > 0 ? (
                      orderDetails.labtestPrescriptionDetails.map((url: string, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl w-full"
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="text-rose-500 w-6 h-6" />
                            <p className="text-xs font-bold text-slate-800">Prescription #{idx + 1}</p>
                          </div>
                          <button
                            onClick={() => setPdfPreviewUrl(url)}
                            className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 border-none cursor-pointer"
                          >
                            View Document
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-100 rounded-xl w-full text-slate-500 text-xs font-semibold">
                        <Info size={16} className="text-amber-500" />
                        Prescription is required/uploaded, but no document link is available.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Order Invoice / Bill */}
            {orderDetails.billUrl && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowBill(!showBill)}
                >
                  <span className="uppercase tracking-wider">Order Invoice / Bill</span>
                  {showBill ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showBill && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 flex items-center justify-between bg-slate-50/50">
                    <div className="flex items-center gap-2">
                      <FileText className="text-rose-500 w-8 h-8" />
                      <div>
                        <p className="text-xs font-bold text-slate-800">Invoice Bill PDF</p>
                        <p className="text-[10px] text-slate-400">Official tax receipt generated for Labtest</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setPdfPreviewUrl(orderDetails.billUrl)}
                      className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all cursor-pointer border-none"
                    >
                      👁️ Preview Bill
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Uploaded Reports */}
            {orderDetails.labtestReportDetails && orderDetails.labtestReportDetails.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowReports(!showReports)}
                >
                  <span className="uppercase tracking-wider">Uploaded Test Reports</span>
                  {showReports ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showReports && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 flex flex-col gap-2">
                    {orderDetails.labtestReportDetails.map((url: string, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="text-emerald-500 w-6 h-6" />
                          <p className="text-xs font-bold text-slate-800">Uploaded Report Document #{idx + 1}</p>
                        </div>
                        <button
                          onClick={() => setPdfPreviewUrl(url)}
                          className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-100 transition-colors cursor-pointer"
                        >
                          Preview Report
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Payment Breakdown */}
          <div className="w-full lg:w-[320px] flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col gap-4 sticky top-5">
              <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                Payment Breakdown
              </h4>

              <div className="flex flex-col gap-3.5 text-xs font-semibold text-slate-500">
                <div className="flex justify-between">
                  <span>MRP Total</span>
                  <span className="text-slate-800 font-bold">
                    ₹{orderDetails.paymentSummary?.mrpTotal || orderDetails.orderTotal}
                  </span>
                </div>
                <div className="flex justify-between text-emerald-600 bg-emerald-50/50 px-2 py-1.5 rounded-lg border border-emerald-100/50">
                  <span>Discount</span>
                  <span className="font-extrabold">-₹{orderDetails.paymentSummary?.discount || "0"}</span>
                </div>
                {orderDetails.paymentSummary?.coinsUsed > 0 && (
                  <div className="flex justify-between">
                    <span>Coins Applied</span>
                    <span className="text-slate-800 font-bold">-{orderDetails.paymentSummary.coinsUsed}</span>
                  </div>
                )}

                <hr className="border-slate-100 my-0.5" />

                <div className="flex justify-between items-center text-sm font-extrabold text-slate-800 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span>Amount Paid</span>
                  <span className="text-lg font-black text-emerald-600">₹{orderDetails.orderTotal}</span>
                </div>

                <div className="mt-2 bg-slate-50/50 rounded-xl border border-slate-100 p-3 flex flex-col gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  <div className="flex justify-between">
                    <span>Payment Method</span>
                    <span className="text-slate-600 font-extrabold">{orderDetails.paymentMethod || "COD"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Payment Status</span>
                    <span
                      className={`font-extrabold ${
                        orderDetails.paymentStatus?.toLowerCase() === "success" ? "text-emerald-600" : "text-amber-600"
                      }`}
                    >
                      {orderDetails.paymentStatus || "Pending"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions in Sidebar */}
              {isClinicAssigned && (
                <div className="mt-2 pt-3 border-t border-slate-100">
                  <button
                    onClick={handleAssignClick}
                    disabled={!selectedDriver || assignLoading}
                    className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-xs font-bold transition-all border-none ${
                      selectedDriver
                        ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-200/50 hover:-translate-y-0.5 cursor-pointer"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                  >
                    <Truck size={14} />
                    {assignLoading ? "Assigning..." : "Assign Rider & Proceed"}
                  </button>
                </div>
              )}

              {isPickupCompleted && (
                <div className="mt-2 pt-3 border-t border-slate-100">
                  <button
                    onClick={handleForwardClick}
                    disabled={forwardLoading}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-xs font-bold bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-200/50 transition-all border-none hover:-translate-y-0.5 cursor-pointer"
                  >
                    <ArrowRight size={14} />
                    {forwardLoading ? "Forwarding..." : "Forward Samples to Lab"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document/Report PDF Preview Modal */}
      {pdfPreviewUrl && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[300000] flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setPdfPreviewUrl(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Document Preview</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                onClick={() => setPdfPreviewUrl(null)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 bg-slate-100">
              {isPDF(pdfPreviewUrl) ? (
                <iframe
                  src={`${pdfPreviewUrl}#toolbar=0`}
                  className="w-full h-full border-none"
                  title="Document Preview PDF"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                  <img src={pdfPreviewUrl} alt="Preview" className="max-w-full max-h-full object-contain rounded-lg" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* RIDER SELECT MODAL */}
      {showRiderModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[300000] flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setShowRiderModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-[480px] w-full max-h-[80vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Select Available Rider</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-colors border-none cursor-pointer"
                onClick={() => setShowRiderModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            {/* Rider Search */}
            <div className="px-5 py-3 border-b border-slate-100 bg-white">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search available riders by name..."
                  value={riderSearch}
                  onChange={(e) => setRiderSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Riders List */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-white">
              {drivers.filter((d) => d.name.toLowerCase().includes(riderSearch.toLowerCase())).length === 0 ? (
                <div className="text-center py-6 text-xs text-slate-400 italic">No riders found.</div>
              ) : (
                drivers
                  .filter((d) => d.name.toLowerCase().includes(riderSearch.toLowerCase()))
                  .map((driver) => {
                    const isCurrent = driver.id === selectedDriver;
                    return (
                      <div
                        key={driver.id}
                        onClick={() => {
                          onDriverSelect(driver);
                          setShowRiderModal(false);
                        }}
                        className={`flex items-center justify-between p-3.5 rounded-xl border transition-all cursor-pointer group ${
                          isCurrent
                            ? "border-indigo-500 bg-indigo-50/40"
                            : "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/10"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={driver.profilePicture || "https://via.placeholder.com/40"}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover border border-slate-200 bg-white"
                          />
                          <div>
                            <div className="text-xs font-bold text-slate-800">{driver.name}</div>
                            <div className="text-[10px] text-slate-500 font-semibold mt-0.5">📞 {driver.mobileNumber}</div>
                          </div>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-1 rounded bg-slate-100 text-[10px] font-bold text-slate-500 border border-slate-200/50">
                          {driver.pendingOrdersCount} ongoing
                        </span>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicLabtestOrderDetailsView;